import { IDocumentTokenDto } from "@/models/document-token.entity";
import { BaseTokenEntity, ITokenEntity } from "./base-token.entity";
import { IHistory, TemplateHistoryAction } from "./token-history";
import { QrSignatureTokenEntity } from "./qr-signature-token.entity";
import { ImageSignatureTokenEntity } from "./image-signature-token.entity";
import { FreitextTokenEntity } from "./freitext-token.entity";
import { VuetifyMaterialColorEnum } from "@/lib/enum/vuetify-color.enum";
import { CheckboxTokenEntity } from "./checkbox-token.entity";
import { PlaceholderTokenEntity } from "./placeholder-token.entity";

export class DocumentPageEntity {
  pageIndex: number;
  /** URL of the source PDF — used by PdfViewer to render this page. */
  pdfUrl: string;
  widthMm: number;
  heightMm: number;
  tokens: BaseTokenEntity[];
  private _currentTokenIndex = -1;
  private _history: IHistory | null = null;

  constructor(data: {
    pageIndex: number;
    pdfUrl: string;
    widthMm: number;
    heightMm: number;
    tokens?: BaseTokenEntity[];
    currentTokenIndex?: number;
    history?: IHistory;
  }) {
    this.pageIndex = data.pageIndex;
    this.pdfUrl = data.pdfUrl;
    this.widthMm = data.widthMm;
    this.heightMm = data.heightMm;
    this._history = data.history ?? null;
    this.tokens = (data.tokens ?? []).map((token) => token.clone(this._history ?? undefined));
    this.currentTokenIndex = data.currentTokenIndex ?? -1;
  }

  get fingerprint(): string {
    return `${this.pageIndex}:${this.currentTokenIndex}:${this.tokens.map((t) => t.fingerprint).join(";")}`;
  }

  get pdfWidth(): number {
    return this.widthMm;
  }

  get pdfHeight(): number {
    return this.heightMm;
  }

  get currentTokenIndex(): number {
    return this._currentTokenIndex;
  }

  set currentTokenIndex(index: number) {
    this.setCurrentToken(index);
  }

  get currentToken(): BaseTokenEntity | undefined {
    return this.tokens[this._currentTokenIndex];
  }

  setCurrentToken(index: number): void {
    if (index < -1 || index >= this.tokens.length) {
      this._currentTokenIndex = -1;
      return;
    }
    this._currentTokenIndex = index;
  }

  addToken(token: BaseTokenEntity): BaseTokenEntity {
    const next = token.clone(this._history ?? undefined);
    next.validateConstraints(this.widthMm, this.heightMm);
    this.tokens.push(next);
    this._history?.push(TemplateHistoryAction.ADD_TOKEN, "Added token");
    return next;
  }

  updateToken(id: string, patch: Partial<ITokenEntity>): void {
    const token = this.getToken(id);
    if (!token) throw new Error(`Token not found: ${id}`);
    Object.assign(token, patch);
    if (!token.isEditable) {
      token.isRequired = false;
    }
    token.validateConstraints(this.widthMm, this.heightMm);
    this._history?.push(TemplateHistoryAction.UPDATE_TOKEN, "Updated token");
  }

  previewToken(id: string, patch: Partial<ITokenEntity>): void {
    const token = this.getToken(id);
    if (!token) return;
    Object.assign(token, patch);
    if (!token.isEditable) {
      token.isRequired = false;
    }
    token.validateConstraints(this.widthMm, this.heightMm);
  }

  changeTokenType(token: BaseTokenEntity, newTokenString: string, defaultTitle?: string): void {
    const replacement = DocumentPageEntity.tokenFromTokenString(newTokenString, {
      x: token.x,
      y: token.y,
      w: token.w,
      h: token.h,
      fontSize: token.fontSize,
      value: newTokenString === CheckboxTokenEntity.token ? (token.value ?? "") : undefined
    });
    replacement.id = token.id;
    replacement.title = token.title || defaultTitle || "";
    replacement.isEditable = token.isEditable;
    replacement.isRequired = replacement.isEditable ? token.isRequired : false;
    replacement.description = token.description;
    this.replaceToken(token.id, replacement);
  }

  replaceToken(id: string, replacement: BaseTokenEntity): void {
    const idx = this.tokens.findIndex((token) => token.id === id);
    if (idx < 0) throw new Error(`Token not found: ${id}`);
    const next = replacement.clone(this._history ?? undefined);
    next.validateConstraints(this.widthMm, this.heightMm);
    this.tokens.splice(idx, 1, next);
    this._history?.push(TemplateHistoryAction.UPDATE_TOKEN, "Replaced token");
  }

  removeToken(id: string): void {
    const idx = this.tokens.findIndex((token) => token.id === id);
    if (idx >= 0) {
      this.tokens.splice(idx, 1);
      if (this._currentTokenIndex === idx) {
        this._currentTokenIndex = -1;
      } else if (this._currentTokenIndex > idx) {
        this._currentTokenIndex -= 1;
      }
      this._history?.push(TemplateHistoryAction.REMOVE_TOKEN, "Removed token");
    }
  }

  getToken(id: string): BaseTokenEntity | undefined {
    return this.tokens.find((token) => token.id === id);
  }

  clone(history?: IHistory): DocumentPageEntity {
    return new DocumentPageEntity({
      pageIndex: this.pageIndex,
      pdfUrl: this.pdfUrl,
      widthMm: this.widthMm,
      heightMm: this.heightMm,
      tokens: this.tokens.map((token) => token.clone()),
      currentTokenIndex: this.currentTokenIndex,
      history
    });
  }

  equals(other: DocumentPageEntity): boolean {
    if (this.pageIndex !== other.pageIndex) return false;
    if (this.currentTokenIndex !== other.currentTokenIndex) return false;
    if (this.tokens.length !== other.tokens.length) return false;
    return this.tokens.every((token, i) => token.equals(other.tokens[i]));
  }

  hasTokensWithoutValue(): boolean {
    return this.tokens.some((token) => !(token.value ?? "").trim());
  }

  toDto(): IDocumentTokenDto[] {
    return this.tokens.map((token) => token.toDto(this.widthMm, this.heightMm));
  }

  static colorHexForTokenString(token: string | null): string {
    if (!token) return VuetifyMaterialColorEnum.GREY_DARKEN_1;
    const entity = DocumentPageEntity.tokenFromTokenString(token, { x: 0, y: 0, w: 0, h: 0 });
    return entity.colorHex;
  }

  static displayLabelForToken(token: string, translate: (key: string) => string): string {
    if (!token) return "";
    const stripped = token.replaceAll("{", "").replaceAll("}", "").trim();
    if (stripped.includes("#") && stripped.includes(" ") && stripped.includes("/")) {
      const bits = stripped.split(" ");
      const formatter = bits[0].replaceAll("#", "");
      const path = bits[1] ?? "";
      const pathLabel = translate(`components.template.editor.placeholder.path.${path}`) || path;
      const formatLabel = translate(`components.template.editor.placeholder.formatters.${formatter}`) || formatter;
      return `${pathLabel} ${formatLabel}`.trim();
    }
    if (PlaceholderTokenEntity.isPlaceholder(token) && token !== PlaceholderTokenEntity.SENTINEL) {
      return translate(`components.template.editor.placeholder.path.${stripped}`) || stripped;
    }
    const entity = DocumentPageEntity.tokenFromTokenString(token, { x: 0, y: 0, w: 0, h: 0 });
    const labelKey = entity.labelKey;
    return labelKey ? translate(labelKey) || token : token;
  }

  static tokenFromTokenString(
    tokenString: string,
    data: { x: number; y: number; w: number; h: number; fontSize?: number; value?: string }
  ): BaseTokenEntity {
    const base = { ...data, isRequired: false, isEditable: true };
    switch (tokenString) {
      case QrSignatureTokenEntity.token:
        return new QrSignatureTokenEntity(base);
      case ImageSignatureTokenEntity.token:
        return new ImageSignatureTokenEntity(base);
      case FreitextTokenEntity.token:
        return new FreitextTokenEntity(base);
      case CheckboxTokenEntity.token:
        return new CheckboxTokenEntity(base);
      default:
        if (tokenString === PlaceholderTokenEntity.SENTINEL || PlaceholderTokenEntity.isPlaceholder(tokenString)) {
          return PlaceholderTokenEntity.fromTokenString(tokenString, base);
        }
        if (tokenString && tokenString !== "") {
          return new FreitextTokenEntity({ ...base, value: base.value ?? tokenString });
        }
        return new FreitextTokenEntity(base);
    }
  }

  static tokenFromDto(dto: IDocumentTokenDto, widthMm: number, heightMm: number): BaseTokenEntity {
    const tokenString = dto.token ?? "";
    const coords = {
      x: widthMm > 0 ? dto.coordinates.x / widthMm : 0,
      y: heightMm > 0 ? dto.coordinates.y / heightMm : 0,
      w: widthMm > 0 ? dto.coordinates.w / widthMm : 0,
      h: heightMm > 0 ? dto.coordinates.h / heightMm : 0
    };
    const entity = DocumentPageEntity.tokenFromTokenString(tokenString, {
      ...coords,
      fontSize: dto.coordinates.fontSize,
      value: dto.value
    });
    entity.id = (dto as { id?: string }).id ?? entity.id;
    entity.isEditable = dto.isEditable ?? true;
    entity.isRequired = entity.isEditable ? (dto.isRequired ?? false) : false;
    entity.title = dto.title;
    entity.description = dto.description;
    entity.validateConstraints(widthMm, heightMm);
    return entity;
  }

  static fromDto(
    dtos: IDocumentTokenDto[],
    pdfUrl: string,
    widthMm: number,
    heightMm: number,
    pageIndex = 0,
    currentTokenIndex = -1,
    history?: IHistory
  ): DocumentPageEntity {
    return new DocumentPageEntity({
      pageIndex,
      pdfUrl,
      widthMm,
      heightMm,
      tokens: dtos.map((dto) => DocumentPageEntity.tokenFromDto(dto, widthMm, heightMm)),
      currentTokenIndex,
      history
    });
  }

  // --- geometry helpers -----------------------------------------------------

  /** Height / width ratio of the page in document space. */
  get aspectRatio(): number {
    return this.widthMm > 0 ? this.heightMm / this.widthMm : Math.SQRT2;
  }

  /**
   * Canvas pixel dimensions when rendered at `scale` by pdfjs.
   * pdfjs viewport width = (widthMm / 25.4) * 72 * scale  (mm → pt → px).
   * Callers use this to size canvases and SVG viewBoxes without touching pdfjs directly.
   */
  canvasSize(scale: number): { w: number; h: number } {
    const ptPerMm = 72 / 25.4;
    return {
      w: Math.round(this.widthMm * ptPerMm * scale),
      h: Math.round(this.heightMm * ptPerMm * scale)
    };
  }
}
